import type { ILogLayer } from '@repo/observability/client-logger'
import { delay, ensureError, withTimeout } from '@repo/utils'
import {
  type Account,
  type Address,
  type Chain,
  type Client,
  createPublicClient,
  custom,
  type Hash,
  type Hex,
  isHash,
  isHex,
  type PublicClient,
  size,
  type Transport,
  WaitForCallsStatusTimeoutError
} from 'viem'
import {
  estimateGas,
  sendCalls,
  waitForCallsStatus,
  type WaitForCallsStatusReturnType,
  waitForTransactionReceipt
} from 'viem/actions'

import type { AttributionIssueReporter } from './resolveAttribution'

import { DEFAULT_TX_TIMEOUT, SAFE_WALLET_TX_TIMEOUT } from '../../../hooks/safe-wallet.hooks'
import { hasReceiptOrFailed } from '../../../utils'
import {
  classifyGasEstimateError,
  GAS_ESTIMATE_TIMEOUT_MS
} from '../../../utils/gas-estimation.utils'
import {
  checkSafeProposal,
  findSafeProposal,
  type SafeProposal,
  SafeTxServiceRateLimitError
} from '../../../utils/safe-proposal.utils'
import { sendWalletTransaction } from '../../../utils/sendWalletTransaction'
import {
  ActionFlowError,
  type ActionFlowReceipt,
  type ActionFlowGasPolicy,
  type ActionFlowTxGas,
  BatchTxBundleError,
  type CallRequest,
  type CallDetails,
  type ResolvedCall,
  type Step
} from '../types.action-flow'
import { addCalldataAttribution } from './addCalldataAttribution'
import { raceAbort } from './raceAbort'

/** Deadline for finding the proposal in the Safe Transaction Service. */
const SAFE_PROPOSAL_LOOKUP_TIMEOUT_MS = 15_000
const SAFE_PROPOSAL_POLL_INITIAL_INTERVAL_MS = 5_000
const SAFE_PROPOSAL_POLL_MAX_INTERVAL_MS = 60_000
const SAFE_PROPOSAL_POLL_BACKOFF = 1.5
const SAFE_PROPOSAL_MAX_RETRY_AFTER_MS = 300_000
const SAFE_PROPOSAL_DEFAULT_RETRY_AFTER_MS = 60_000
const SAFE_PROPOSAL_CHECK_TIMEOUT_MS = 15_000
/** Consecutive failed status checks before the stall is logged. */
const SAFE_PROPOSAL_CHECK_FAILURES_BEFORE_WARNING = 5
const SAFE_STATUS_UNAVAILABLE_LOG_EVENT = 'action_flow.safe_status_unavailable'

type TxPending = { hash?: Hex; bundleId?: string; safeTxHash?: Hash }

interface ExecuteStepParameters {
  publicClient: Client<Transport, Chain>
  walletClient: Client<Transport, Chain, Account>
  step: Step
  onTxPending: (pending: TxPending) => void
  callDetails?: CallDetails
  isSafeWallet?: boolean
  gasPolicy?: ActionFlowGasPolicy
  walletManagesGas?: boolean
  signal?: AbortSignal
  /**
   * Pre-validated 4-byte origin tag from `resolveAttributionOriginTag`. When set,
   * every outer call's calldata is suffixed before it's estimated/sent (single
   * path) or handed to the wallet (batch). Absent when the flow opted out.
   */
  originTag?: Hex
  /** Reports per-call attribution skips (e.g. malformed calldata). */
  onAttributionIssue?: AttributionIssueReporter
  logger?: ILogLayer
}

/**
 * Apply the attribution suffix to one outer call at the last app-controlled
 * submission boundary. No-op when the flow opted out (`originTag` absent) or for
 * empty `0x` calldata; non-empty shorter-than-selector calldata is reported as a
 * malformed-call signal before skipping. Otherwise suffixed via
 * `addCalldataAttribution`.
 */
function applyAttribution(
  call: ResolvedCall,
  originTag: Hex | undefined,
  onAttributionIssue?: AttributionIssueReporter
): ResolvedCall {
  if (!originTag) return call

  const dataSize = size(call.data)
  if (dataSize === 0) return call
  if (dataSize < 4) {
    onAttributionIssue?.(
      'Skipped attribution for malformed calldata — shorter than a 4-byte selector',
      { to: call.to, dataSize }
    )
    return call
  }

  return { ...call, data: addCalldataAttribution(call.data, originTag) }
}

type ExecuteStepReturnType =
  | { type: 'signature'; signature: Hex }
  | {
      type: 'tx'
      hash: Hex
      status: 'success' | 'reverted'
      receipt: ActionFlowReceipt
      gas?: ActionFlowTxGas
    }

function toActionFlowReceipt(receipt: {
  transactionHash: Hex
  blockHash: Hex
  blockNumber: bigint
  gasUsed: bigint
  status: 'success' | 'reverted'
  logs: readonly { address: string; data: string; topics: readonly string[] }[]
}): ActionFlowReceipt {
  return {
    transactionHash: receipt.transactionHash,
    blockHash: receipt.blockHash,
    blockNumber: receipt.blockNumber,
    gasUsed: receipt.gasUsed,
    status: receipt.status,
    logs: receipt.logs.map(log => ({
      address: log.address as Address,
      data: log.data as Hex,
      topics: log.topics as readonly Hex[]
    }))
  }
}

export async function executeStep({
  publicClient,
  walletClient,
  step,
  onTxPending,
  callDetails,
  isSafeWallet,
  gasPolicy,
  walletManagesGas,
  signal,
  originTag,
  onAttributionIssue,
  logger
}: ExecuteStepParameters): Promise<ExecuteStepReturnType> {
  // Short-circuit before invoking viem when the user already closed the dialog —
  // avoids issuing wallet/RPC requests we'd immediately discard. The in-flight
  // race in `raceAbort` covers the same intent for already-running calls.
  if (signal?.aborted) {
    throw new ActionFlowError('Execution aborted', { reportable: false })
  }

  switch (step.type) {
    case 'signature': {
      const signature = await raceAbort(step.sign(walletClient), signal)
      return { type: 'signature', signature }
    }
    case 'singleTx': {
      const rawCall = callDetails && 'call' in callDetails ? callDetails.call : step.getCall()
      // Tag once, then feed the *same* object into estimateGas and
      // sendTransaction so the buffered gas matches the sent calldata.
      const call = applyAttribution(rawCall, originTag, onAttributionIssue)
      const { hash, status, receipt, gas } = await executeSingleCallRequest({
        walletClient,
        publicClient,
        call,
        gasBuffer: walletManagesGas ? undefined : (step.gasBuffer ?? gasPolicy?.buffer),
        gasPolicy,
        onTxPending,
        signal,
        logger
      })
      return { type: 'tx', hash, status, receipt, ...(gas !== undefined && { gas }) }
    }
    case 'batchTx': {
      const rawCalls =
        callDetails && 'calls' in callDetails ? callDetails.calls : step.getCalls.map(req => req())
      // Tag each outer call's data in the capability payload before sendCalls;
      // the wallet / smart-account still determines the final on-chain envelope.
      const calls = rawCalls.map(call => applyAttribution(call, originTag, onAttributionIssue))
      const { hash, status, receipt } = await executeAtomicBatchCallRequests({
        walletClient,
        calls,
        onTxPending,
        isSafeWallet,
        signal
      })
      return { type: 'tx', hash, status, receipt, gas: { gasLimitSource: 'wallet' } }
    }
  }
}

async function executeSingleCallRequest({
  publicClient,
  walletClient,
  call,
  gasBuffer,
  gasPolicy,
  onTxPending,
  signal,
  logger
}: {
  publicClient: ExecuteStepParameters['publicClient']
  walletClient: ExecuteStepParameters['walletClient']
  call: ResolvedCall
  gasBuffer?: CallRequest['gasBuffer']
  gasPolicy?: ActionFlowGasPolicy
  onTxPending: (pending: TxPending) => void
  signal?: AbortSignal
  logger?: ILogLayer
}): Promise<{
  hash: Hex
  status: 'success' | 'reverted'
  receipt: ActionFlowReceipt
  gas?: ActionFlowTxGas
}> {
  // The wallet validates gas; sponsorship can let a zero-balance wallet still send.

  let txCall = call
  let gas: ActionFlowTxGas = { gasLimitSource: 'wallet' }
  if (gasBuffer) {
    try {
      const gasEstimate = await raceAbort(
        withTimeout(
          estimateGas(publicClient, {
            account: walletClient.account,
            ...call
          }),
          GAS_ESTIMATE_TIMEOUT_MS
        ),
        signal
      )
      const bufferedGas =
        typeof gasBuffer === 'function'
          ? gasBuffer(gasEstimate)
          : BigInt(Math.ceil(Number(gasEstimate) * gasBuffer))
      txCall = { ...call, gas: bufferedGas }
      gas = { gasLimitSource: 'app_buffered', gasEstimate, gasLimit: bufferedGas }
    } catch (error) {
      if (signal?.aborted) throw error
      const gasEstimateError = classifyGasEstimateError(error)
      if (gasEstimateError === 'reverted' && gasPolicy?.onRevertedEstimate !== 'send') {
        throw error
      }
      gas = {
        gasLimitSource: 'estimate_failed',
        gasEstimateError
      }
    }
  } else if (call.gas !== undefined) {
    gas = { gasLimitSource: 'builder', gasLimit: call.gas }
  }

  // Typed as unknown: some wallets answer with something other than a tx hash.
  const result: unknown = await raceAbort(
    sendWalletTransaction(walletClient, txCall, () => signal?.aborted === true),
    signal
  )
  if (typeof result === 'string' && isHash(result)) {
    onTxPending({ hash: result })
    const receipt = await raceAbort(
      waitForTransactionReceipt(publicClient, { hash: result }),
      signal
    )
    // A sped-up or replaced transaction is mined under another hash than the one sent.
    return {
      hash: receipt.transactionHash,
      status: receipt.status,
      receipt: toActionFlowReceipt(receipt),
      ...(receipt.transactionHash === result && { gas })
    }
  }

  // The Safe helpers call bound actions, which the engine's wagmi `Client` lacks.
  const safeClient = createPublicClient({
    chain: publicClient.chain,
    transport: custom(publicClient)
  })
  const proposal = await resolveSafeProposal({
    publicClient,
    safeClient,
    walletClient,
    call: txCall,
    result,
    signal
  })
  onTxPending({ safeTxHash: proposal.safeTxHash })
  return trackSafeProposal({ publicClient, safeClient, proposal, signal, logger })
}

/**
 * Narrows a non-hash `sendTransaction` result to the Safe transaction it proposed.
 * A wallet fronting a Safe (e.g. Rabby's institutional mode) proposes the call to
 * the Safe and answers with the owner's 65-byte signature, which identifies the
 * queued proposal in the Safe Transaction Service. Never hands the signature to
 * receipt polling, which would never resolve.
 * @throws ActionFlowError `indeterminate-submission` for any other non-hash result,
 * or `safe-proposal-queued` when the Safe proposal can't be found, so the user
 * finishes it from the Safe queue instead of resending.
 */
async function resolveSafeProposal({
  publicClient,
  safeClient,
  walletClient,
  call,
  result,
  signal
}: {
  publicClient: ExecuteStepParameters['publicClient']
  safeClient: PublicClient
  walletClient: ExecuteStepParameters['walletClient']
  call: ResolvedCall
  result: unknown
  signal?: AbortSignal
}): Promise<SafeProposal> {
  if (typeof result !== 'string' || !isHex(result) || size(result) !== 65) {
    throw new ActionFlowError(
      'Unexpected response instead of a transaction hash. Check your wallet or Safe for the transaction. If nothing was sent, close this window and start again.',
      { errorType: 'indeterminate-submission' }
    )
  }

  let lookupError: unknown
  const proposal = await raceAbort(
    withTimeout(
      findSafeProposal({
        client: safeClient,
        chainId: publicClient.chain.id,
        safe: walletClient.account.address,
        signature: result,
        tx: { to: call.to, data: call.data, value: call.value }
      }),
      SAFE_PROPOSAL_LOOKUP_TIMEOUT_MS
    ).catch((e: unknown) => {
      lookupError = e
      return undefined
    }),
    signal
  )
  if (!proposal) {
    throw new ActionFlowError(
      'Transaction queued in your Safe. Confirm and execute it from your Safe queue. Do not send it again.',
      { reportable: false, errorType: 'safe-proposal-queued', cause: lookupError }
    )
  }
  return proposal
}

/**
 * Polls a Safe proposal until the Safe executes, rejects or replaces it. There is
 * no deadline, as a Safe can take days to collect signatures; the flow's abort
 * signal stops it. Rounds back off from 5 s to 60 s to stay within the
 * Transaction Service rate limits, and a 429 waits out its `Retry-After`. Other
 * failed or stalled checks (e.g. Transaction Service hiccups) retry on the next
 * round; a run of failed checks is logged once as a warning.
 * @returns the executing transaction's hash and receipt, `reverted` when the
 * Safe's own execution failed.
 * @throws ActionFlowError `safe-proposal-dropped` when the proposal was deleted,
 * rejected or replaced.
 */
async function trackSafeProposal({
  publicClient,
  safeClient,
  proposal,
  signal,
  logger
}: {
  publicClient: ExecuteStepParameters['publicClient']
  safeClient: PublicClient
  proposal: SafeProposal
  signal?: AbortSignal
  logger?: ILogLayer
}): Promise<{ hash: Hex; status: 'success' | 'reverted'; receipt: ActionFlowReceipt }> {
  let intervalMs = SAFE_PROPOSAL_POLL_INITIAL_INTERVAL_MS
  let consecutiveFailures = 0
  for (;;) {
    let retryAfterMs = 0
    const outcome = await raceAbort(
      withTimeout(
        checkSafeProposal({ client: safeClient, chainId: publicClient.chain.id, proposal, signal }),
        SAFE_PROPOSAL_CHECK_TIMEOUT_MS
      ).then(
        result => {
          consecutiveFailures = 0
          return result
        },
        (error: unknown) => {
          if (error instanceof SafeTxServiceRateLimitError) {
            retryAfterMs = Math.min(
              error.retryAfterMs ?? SAFE_PROPOSAL_DEFAULT_RETRY_AFTER_MS,
              SAFE_PROPOSAL_MAX_RETRY_AFTER_MS
            )
          }
          consecutiveFailures += 1
          if (
            consecutiveFailures === SAFE_PROPOSAL_CHECK_FAILURES_BEFORE_WARNING &&
            !signal?.aborted
          ) {
            logger
              ?.withError(ensureError(error))
              .withMetadata({ safeTxHash: proposal.safeTxHash, consecutiveFailures })
              .warn(SAFE_STATUS_UNAVAILABLE_LOG_EVENT)
          }
          return undefined
        }
      ),
      signal
    )
    if (outcome?.status === 'dropped') {
      throw new ActionFlowError(
        'Your Safe rejected or replaced this transaction. Submit it again to create a new proposal.',
        { reportable: false, errorType: 'safe-proposal-dropped' }
      )
    }
    if (outcome) {
      // The outer transaction succeeds even when the Safe's inner call fails, so
      // the Safe's execution event decides the status.
      const status = outcome.status === 'executed' ? 'success' : 'reverted'
      return {
        hash: outcome.hash,
        status,
        receipt: toActionFlowReceipt({ ...outcome.receipt, status })
      }
    }
    await raceAbort(delay(Math.max(intervalMs, retryAfterMs)), signal)
    intervalMs = Math.min(
      Math.round(intervalMs * SAFE_PROPOSAL_POLL_BACKOFF),
      SAFE_PROPOSAL_POLL_MAX_INTERVAL_MS
    )
  }
}

async function executeAtomicBatchCallRequests({
  walletClient,
  calls,
  onTxPending,
  isSafeWallet,
  signal
}: {
  walletClient: ExecuteStepParameters['walletClient']
  calls: ResolvedCall[]
  onTxPending: (pending: TxPending) => void
  isSafeWallet?: boolean
  signal?: AbortSignal
}): Promise<{ hash: Hex; status: 'success' | 'reverted'; receipt: ActionFlowReceipt }> {
  // No gas-fee validation for an atomic batch: gas may be sponsored, so a
  // wallet with no gas can still send the transaction.

  const { id } = await raceAbort(
    sendCalls(walletClient, {
      calls,
      forceAtomic: true
    }),
    signal
  )
  onTxPending({ bundleId: id })

  const sendCallsResolvedAt = Date.now()
  let statusResult: Awaited<WaitForCallsStatusReturnType>
  try {
    statusResult = await raceAbort(
      waitForCallsStatus(walletClient, {
        id,
        pollingInterval: 1000,
        timeout: isSafeWallet ? SAFE_WALLET_TX_TIMEOUT : DEFAULT_TX_TIMEOUT,
        ...(isSafeWallet ? { status: hasReceiptOrFailed } : {})
      }),
      signal
    )
  } catch (e) {
    if (e instanceof ActionFlowError) throw e
    if (isSafeWallet && e instanceof WaitForCallsStatusTimeoutError) {
      throw new ActionFlowError('Safe transaction expired', { reportable: false })
    }
    const cause = ensureError(e)
    throw new BatchTxBundleError(cause.message, {
      debug: { bundleId: id, sendCallsToFailureMs: Date.now() - sendCallsResolvedAt },
      cause
    })
  }
  if (isSafeWallet && statusResult.statusCode === 400) {
    throw new ActionFlowError('Safe transaction replaced', {
      reportable: false,
      errorType: 'safe-replaced'
    })
  }

  const { status, receipts } = statusResult

  const firstReceipt = receipts?.[0]
  const hash = firstReceipt?.transactionHash
  if (!status || status === 'pending' || !hash || !firstReceipt) {
    throw new BatchTxBundleError('Unexpected batch call result', {
      debug: { bundleId: id, sendCallsToFailureMs: Date.now() - sendCallsResolvedAt },
      cause: statusResult,
      notIncluded: status === 'failure'
    })
  }

  const resolvedStatus = status === 'success' ? 'success' : 'reverted'
  return {
    hash,
    status: resolvedStatus,
    receipt: toActionFlowReceipt({ ...firstReceipt, status: resolvedStatus })
  }
}
